import { DeleteOutlined, TeamOutlined } from '@ant-design/icons';
import { theme } from 'antd';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectPeople } from 'reduxStore/people/selectors';
import { selectTeamsByTeamID } from 'reduxStore/teams/selectors';
import { teamsActions } from 'reduxStore/teams/slice';
import { Button, Col, Flex, List, Modal, Row, Select, Typography } from 'zui/Antd';
import { ConditionalTooltipButton } from 'zui/Custom';

const { Title, Text } = Typography;

const AddPeopleToTeamModal = ({ open, setOpen, children, teamID }) => {
	const dispatch = useDispatch();
	const {
		token: {
			fontSizeSM,
			marginLG,
			paddingXL,
			padding,
			paddingLG,
			paddingXS,
			paddingMD,
			colorTextDescription,
			colorText,
			paddingSM
		}
	} = theme.useToken();

	const team = useSelector((state) => selectTeamsByTeamID(state, teamID));
	const people = useSelector(selectPeople);

	const availablePeople = people.filter((person) => !team.members?.includes(person.personID));

	const { loadingAddMembersTeam, completeAddMembersTeam } = useSelector((state) => state.Teams);

	const [internalVisible, setInternalVisible] = useState(false);
	const [selectedPeople, setSelectedPeople] = useState([]);

	const visible = open ?? internalVisible;
	const setVisible = setOpen ?? setInternalVisible;

	const handleCancel = useCallback(() => {
		setSelectedPeople([]);
		setVisible(false);
	}, [setVisible]);

	useEffect(() => {
		if (completeAddMembersTeam) {
			handleCancel();
		}
	}, [completeAddMembersTeam, handleCancel]);

	const handleAddPeopleToTeam = useCallback(() => {
		if (selectedPeople.length) {
			dispatch(
				teamsActions.addTeamMember({
					teamID,
					personIDs: selectedPeople.map((p) => p.personID)
				})
			);
		}
	}, [dispatch, selectedPeople, teamID]);

	const handleSelectPerson = (personID) => {
		const person = people.find((p) => p.personID === personID);
		if (person && !selectedPeople.some((p) => p.personID === personID)) {
			setSelectedPeople([...selectedPeople, person]);
		}
	};

	const handleRemovePerson = (personID) => {
		setSelectedPeople(selectedPeople.filter((p) => p.personID !== personID));
	};

	return (
		<Fragment>
			<ConditionalTooltipButton
				buttonText={'Add People'}
				condition={false}
				disabledCondition={false}
				trueTitle={"You don't have permission"}
				falseTitle={'Add people to this team'}
				onClick={() => setVisible(true)}
				icon={<TeamOutlined />}
			>
				{children}
			</ConditionalTooltipButton>

			<Modal
				open={visible}
				onCancel={handleCancel}
				closeIcon={false}
				centered
				destroyOnClose
				footer={[
					<Flex
						align='center'
						justify='flex-end'
						style={{ marginTop: marginLG }}
						gap={paddingSM}
						key='footer'
					>
						<Button onClick={handleCancel} type='default' disabled={loadingAddMembersTeam}>
							Cancel
						</Button>
						<Button
							onClick={handleAddPeopleToTeam}
							type='primary'
							loading={loadingAddMembersTeam}
							disabled={selectedPeople.length === 0}
						>
							Add People
						</Button>
					</Flex>
				]}
				width={472}
				styles={{ content: { padding: `${paddingLG}px ${paddingXL}px` } }}
			>
				<Row gutter={[paddingLG, paddingLG]} align='middle'>
					<Col span={24}>
						<Title level={4} style={{ margin: 0 }}>
							Add People to Team
						</Title>
						<Text style={{ fontSize: fontSizeSM, color: colorTextDescription }}>
							Select the people to add to this team.
						</Text>
					</Col>

					<Col span={24}>
						<Flex vertical gap={padding}>
							<Text strong>People</Text>
							<Select
								showSearch
								placeholder='Enter or Select Person'
								style={{ width: '100%' }}
								options={availablePeople.map((person) => ({
									label: person.displayName,
									value: person.personID
								}))}
								onSelect={handleSelectPerson}
								filterOption={(input, option) =>
									option.label.toLowerCase().includes(input.toLowerCase())
								}
								value={null}
							/>
						</Flex>
					</Col>

					<Col span={24}>
						<Flex vertical gap={paddingMD}>
							<Flex align='center' justify='space-between'>
								<Text strong>Selected People</Text>
								<Button
									type='text'
									onClick={() => setSelectedPeople([])}
									style={{ textDecoration: 'underline' }}
								>
									Clear List
								</Button>
							</Flex>

							<List
								dataSource={selectedPeople}
								loading={loadingAddMembersTeam}
								style={{ minHeight: 186 }}
								renderItem={(person) => (
									<List.Item
										style={{ padding: 0, border: 0, marginBottom: paddingXS }}
										extra={
											<Button
												icon={<DeleteOutlined style={{ fontSize: 16 }} />}
												onClick={() => handleRemovePerson(person.personID)}
												type='text'
												style={{ color: colorText }}
											/>
										}
									>
										<List.Item.Meta
											avatar={null}
											title={<Text style={{ fontWeight: 400 }}>{person.displayName}</Text>}
										/>
									</List.Item>
								)}
							/>
						</Flex>
					</Col>
				</Row>
			</Modal>
		</Fragment>
	);
};

export default AddPeopleToTeamModal;
